import Button from "@components/buttons/Button"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import colors from "@constants/colors"
import { resetButtonStyles } from "@constants/styles"
import { smallcapsBold14Styles } from "@constants/typography"
import { getProductImageAlt } from "@utils/altText"
import { DesktopOnly } from "@utils/MediaQuery"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import DesktopPDPMuxPlayer from "./DesktopMuxPlayer"
import { MuxVideo as MuxVideoType } from "./index"
import ZoomedImageGallery from "./ZoomedImageGallery"

const Container = styled.div`
  flex-direction: column;
  display: flex;
`

const ImageBlock = styled.div``

const Row = styled.div`
  flex-direction: row;
  justify-content: space-between;
  display: flex;
  margin: 24px 0;
`

const MainImage = styled.div`
  ${ImageHelperContainer} {
    width: 100%;
    height: calc(6 / 12 * 100vw * 0.8);
    margin-right: 0;
    background: ${colors.backgroundGray};
    cursor: zoom-in;

    img {
      object-fit: contain;
    }
  }
`

const MainActionImage = styled(MainImage)`
  ${ImageHelperContainer} {
    img {
      object-fit: cover;
    }
  }
`

const SecondaryImage = styled.div`
  ${ImageHelperContainer} {
    height: auto;
    background: ${colors.backgroundGray};
    cursor: zoom-in;

    img {
      object-fit: contain;
    }
  }
`

const SecondaryActionImage = styled(SecondaryImage)`
  ${ImageHelperContainer} {
    img {
      object-fit: cover;
    }
  }
`

const Filler = styled.div`
  height: 5vw;
`

const ActionHeader = styled.div`
  ${smallcapsBold14Styles}
  margin: 63px 0 29px;
  font-size: 16px;
  letter-spacing: 0.5px;
  line-height: 24px;
  color: ${colors.black};
`

const ButtonWrapper = styled.div`
  margin-top: 24px;
`

const ClickableImageWrapperButton = styled.div<{ $secondary?: boolean }>`
  ${resetButtonStyles}

  ${({ $secondary }) =>
    $secondary
      ? `
      width: calc(50% - 16px);
      height: 100%;
        `
      : `
    width: 100%;
    height: 100%;
  `}
`

const renderVariantImageBlock = (
  images: Array<MuxVideoType | string>,
  key: number,
  setMediaToScrollTo: (media: MuxVideoType | string) => void,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void,
  productName?: string,
  deviceName?: string,
  colorName?: string
) => {
  const main = images[0]
  const secondary1 = images[1]
  const secondary2 = images[2]
  return (
    <ImageBlock key={key}>
      {main && (
        <ClickableImageWrapperButton
          aria-label="Zoom in on product image"
          onClick={() => {
            toggleIsZoomedImageGalleryOpen(true)
            setMediaToScrollTo(main)
          }}
          role="button"
        >
          <MainImage>
            <ImageHelper
              alt={getProductImageAlt({
                colorName,
                deviceName,
                imageType: "gallery",
                productName: productName || "Case",
              })}
              src={main as string}
            />
          </MainImage>
        </ClickableImageWrapperButton>
      )}
      <Row>
        {secondary1 && (
          <ClickableImageWrapperButton
            $secondary
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary1)
            }}
            role="button"
          >
            <SecondaryImage>
              <ImageHelper
                alt={getProductImageAlt({
                  colorName,
                  deviceName,
                  imageType: "detail",
                  productName: productName || "Case",
                })}
                src={secondary1 as string}
              />{" "}
            </SecondaryImage>
          </ClickableImageWrapperButton>
        )}
        {secondary2 && typeof secondary2 === "string" && (
          <ClickableImageWrapperButton
            $secondary
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary2)
            }}
            role="button"
          >
            <SecondaryImage>
              <ImageHelper
                alt={getProductImageAlt({
                  colorName,
                  deviceName,
                  imageType: "detail",
                  productName: productName || "Case",
                })}
                src={secondary2}
              />{" "}
            </SecondaryImage>
          </ClickableImageWrapperButton>
        )}
        {/* If not typeof string, we know type is MuxVideoType */}
        {secondary2 && typeof secondary2 !== "string" && (
          <SecondaryImage>
            <DesktopPDPMuxPlayer muxVideo={secondary2} />
          </SecondaryImage>
        )}
      </Row>
    </ImageBlock>
  )
}

const renderVariantImages = (
  images: Array<string>,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void,
  setMediaToScrollTo: (media: MuxVideoType | string) => void,
  imageGalleryMuxVideo?: Array<MuxVideoType>,
  productName?: string,
  deviceName?: string,
  colorName?: string
) => {
  const blocks: JSX.Element[] = []

  // Render first 3 images: [1][2, 3]
  if (images.length > 0) {
    const firstChunk = images.slice(0, 3)
    blocks.push(
      renderVariantImageBlock(
        firstChunk,
        0,
        setMediaToScrollTo,
        toggleIsZoomedImageGalleryOpen,
        productName,
        deviceName,
        colorName
      )
    )
  }

  // If we have a Mux video, render it in its own full-width row after first 3 images
  if (imageGalleryMuxVideo && imageGalleryMuxVideo.length > 0) {
    blocks.push(
      <ImageBlock key="mux-video">
        <MainImage>
          <DesktopPDPMuxPlayer fullWidth muxVideo={imageGalleryMuxVideo[0]} />
        </MainImage>
      </ImageBlock>
    )
  }

  // Then render remaining images in the "large, two small" pattern
  const remainingImages = images.slice(3)
  const chunkedImages = R.splitEvery(3, remainingImages)
  chunkedImages.forEach((chunk, index) => {
    blocks.push(
      renderVariantImageBlock(
        chunk as any,
        index + 2, // Offset key (first chunk = 0, mux video = 1, start at 2)
        setMediaToScrollTo,
        toggleIsZoomedImageGalleryOpen,
        productName,
        deviceName,
        colorName
      )
    )
  })

  return blocks
}

const renderActionImageBlock = (
  media: Array<MuxVideoType | string>,
  key: number,
  setMediaToScrollTo: (media: MuxVideoType | string) => void,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void,
  productName?: string,
  deviceName?: string,
  colorName?: string
) => {
  const secondary1 = media[0]
  const secondary2 = media[1]
  const main = media[2]
  return (
    <ImageBlock key={key}>
      <Row>
        {secondary1 && typeof secondary1 === "string" && (
          <ClickableImageWrapperButton
            $secondary
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary1)
            }}
            role="button"
          >
            <SecondaryActionImage>
              <ImageHelper
                alt={getProductImageAlt({
                  colorName,
                  deviceName,
                  imageType: "lifestyle",
                  productName: productName || "Case",
                })}
                src={secondary1}
              />
            </SecondaryActionImage>
          </ClickableImageWrapperButton>
        )}
        {secondary1 && typeof secondary1 !== "string" && (
          <SecondaryActionImage>
            <DesktopPDPMuxPlayer muxVideo={secondary1} />
          </SecondaryActionImage>
        )}
        {secondary2 && typeof secondary2 === "string" && (
          <ClickableImageWrapperButton
            $secondary
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary2)
            }}
            role="button"
          >
            <SecondaryActionImage>
              <ImageHelper
                alt={getProductImageAlt({
                  colorName,
                  deviceName,
                  imageType: "lifestyle",
                  productName: productName || "Case",
                })}
                src={secondary2}
              />
            </SecondaryActionImage>
          </ClickableImageWrapperButton>
        )}
        {secondary2 && typeof secondary2 !== "string" && (
          <SecondaryActionImage>
            <DesktopPDPMuxPlayer muxVideo={secondary2} />
          </SecondaryActionImage>
        )}
      </Row>
      {main && typeof main === "string" && (
        <ClickableImageWrapperButton
          aria-label="Zoom in on product image"
          onClick={() => {
            toggleIsZoomedImageGalleryOpen(true)
            setMediaToScrollTo(main)
          }}
          role="button"
        >
          <MainActionImage>
            <ImageHelper
              alt={getProductImageAlt({
                colorName,
                deviceName,
                imageType: "action",
                productName: productName || "Case",
              })}
              src={main}
            />
          </MainActionImage>
        </ClickableImageWrapperButton>
      )}
      {main && typeof main !== "string" && (
        <MainActionImage>
          <DesktopPDPMuxPlayer fullWidth muxVideo={main} />
        </MainActionImage>
      )}
    </ImageBlock>
  )
}

const renderActionImages = (
  images: Array<string>,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void,
  setMediaToScrollTo: (mediaItem: MuxVideoType | string) => void,
  lifestyleMuxVideos?: Array<MuxVideoType>,
  productName?: string,
  deviceName?: string,
  colorName?: string
) => {
  let lifestyleMedia: Array<MuxVideoType | string>
  if (images.length > 2 && lifestyleMuxVideos) {
    lifestyleMedia = [
      ...images.slice(0, 2),
      lifestyleMuxVideos[0],
      ...images.slice(2),
      ...lifestyleMuxVideos.slice(1),
    ]
  } else {
    lifestyleMedia = [...images, ...lifestyleMuxVideos!]
  }

  const chunkedMedia = R.splitEvery(3, lifestyleMedia)

  return chunkedMedia.map((chunk, index) => {
    return renderActionImageBlock(
      chunk as any,
      index,
      setMediaToScrollTo,
      toggleIsZoomedImageGalleryOpen,
      productName,
      deviceName,
      colorName
    )
  })
}

interface DesktopProductImagesProps {
  collapseLifestyleImagesUntilExpand?: boolean
  currentVariant: { images: Array<string>; name?: string }
  deviceName?: string
  imageGalleryMuxVideos?: Array<MuxVideoType>
  images: Array<string>
  initialDesktopImages?: number
  lifestyleMuxVideos?: Array<MuxVideoType>
  productName?: string
}

const DesktopProductImages: React.FC<DesktopProductImagesProps> = (props) => {
  const [showAll, setShowAll] = React.useState(false)
  const initialCount = props.initialDesktopImages ?? 3
  const shouldRenderAction =
    showAll || !(props.collapseLifestyleImagesUntilExpand ?? false)

  const variantImages = props.currentVariant?.images ?? []
  const visibleVariantImages = showAll
    ? variantImages
    : variantImages.slice(0, initialCount)

  const actionImages = props.images || []
  const lifestyleMuxVideos = props.lifestyleMuxVideos || []
  const imageGalleryMuxVideos = props.imageGalleryMuxVideos || []

  const [isZoomedImageGalleryOpen, setIsZoomedImageGalleryOpen] =
    React.useState(false)

  const toggleIsZoomedImageGalleryOpen = (zoomedImageGalleryOpen?: boolean) =>
    setIsZoomedImageGalleryOpen(
      zoomedImageGalleryOpen || !isZoomedImageGalleryOpen
    )

  const [mediaToScrollTo, setMediaToScrollTo] = React.useState<
    MuxVideoType | string
  >()

  const zoomedGalleryMedia = [
    ...variantImages,
    ...actionImages,
    ...imageGalleryMuxVideos,
    ...lifestyleMuxVideos,
  ]

  return (
    <DesktopOnly>
      {isZoomedImageGalleryOpen && (
        <ZoomedImageGallery
          media={zoomedGalleryMedia}
          mediaScrollToItem={mediaToScrollTo!}
          toggleIsZoomedImageGalleryOpen={toggleIsZoomedImageGalleryOpen}
        />
      )}
      <Container>
        <Container>
          {renderVariantImages(
            visibleVariantImages,
            toggleIsZoomedImageGalleryOpen,
            setMediaToScrollTo,
            props.imageGalleryMuxVideos!,
            props.productName,
            props.deviceName,
            props.currentVariant?.name
          )}
        </Container>
        {!showAll && variantImages.length > initialCount && (
          <ButtonWrapper>
            <Button
              aria-label="Show all images"
              hideDash
              onClick={() => setShowAll(true)}
              text="Show all images"
              white
            />
          </ButtonWrapper>
        )}
        {variantImages.length === 1 && <Filler />}
        {shouldRenderAction &&
          (actionImages.length > 0 || lifestyleMuxVideos?.length > 0) && (
            <Container>
              <ActionHeader>See it in action</ActionHeader>
              {renderActionImages(
                actionImages,
                toggleIsZoomedImageGalleryOpen,
                setMediaToScrollTo,
                props.lifestyleMuxVideos || [],
                props.productName,
                props.deviceName,
                props.currentVariant?.name
              )}
            </Container>
          )}
      </Container>
    </DesktopOnly>
  )
}

export default DesktopProductImages
